import React from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';

/**
 * PeerSpace-style listing tile — photo, title, social proof, tags, price.
 */
const MarketingListingCard = ({
  href,
  image,
  title,
  subtitle,
  location,
  rating,
  price,
  priceSuffix = 'starting from',
  meta,
  tags = null,
  reviewCount = 0,
  featured = false,
  imageOverlay = null,
  compact = false,
  className = '',
}) => {
  const ratingLabel = rating > 0 ? Number(rating).toFixed(1) : 'New';
  const hasNumericPrice = typeof price === 'number' && price > 0;
  const priceLabel = hasNumericPrice
    ? `$${Number(price).toFixed(0)}`
    : typeof price === 'string'
      ? price
      : null;

  const tagLine =
    tags ||
    [subtitle, location].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') ||
    null;

  return (
    <article className={`marketing-listing-card ${className}`.trim()}>
      <Link to={href} className="group block outline-none">
        <div
          className={`marketing-listing-media relative overflow-hidden bg-m-border-light ${
            compact ? 'aspect-[4/3]' : 'aspect-[5/4]'
          }`}
        >
          {image ? (
            <img
              src={image}
              alt={title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] group-focus-visible:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-m-border-light to-m-canvas-light px-4 text-center text-sm text-m-ink-muted">
              {title || 'No photo'}
            </div>
          )}

          {featured ? (
            <span className="absolute left-3 top-3 z-[1] inline-flex items-center rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-medium tracking-tight text-m-ink shadow-sm">
              Featured
            </span>
          ) : null}

          {imageOverlay ? (
            <div className="absolute right-3 top-3 z-[1]">{imageOverlay}</div>
          ) : null}
        </div>

        <div className="mt-3 space-y-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight text-m-ink">
            {title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-m-ink">
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" aria-hidden />
              <span className="tabular-nums font-medium">{ratingLabel}</span>
              {reviewCount > 0 ? (
                <span className="text-m-ink-muted">({reviewCount})</span>
              ) : null}
            </span>
            {meta ? (
              <>
                <span className="text-m-ink-muted" aria-hidden>
                  ·
                </span>
                <span className="text-m-ink-muted">{meta}</span>
              </>
            ) : null}
          </div>

          {tagLine ? (
            <p className="line-clamp-1 text-[13px] leading-snug text-m-ink-muted">
              {tagLine}
            </p>
          ) : null}

          {priceLabel ? (
            <p className="pt-0.5 text-[15px] leading-snug text-m-ink">
              <span className="font-semibold">
                {hasNumericPrice ? `From ${priceLabel}` : priceLabel}
              </span>
              {hasNumericPrice && priceSuffix ? (
                <span className="ml-1 text-[13px] font-normal text-m-ink-muted">
                  {priceSuffix}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </Link>
    </article>
  );
};

export default MarketingListingCard;
